import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react'
import { Box, Button, Grid, Typography, Input } from '@mui/joy'
import { CallStatus } from '@numeo/types'

export interface DTMFKeypadProps {
    callStatus: CallStatus
    sendDTMF: (digit: string) => Promise<void> | void
}

// Define the ref interface for external components to use
export interface DTMFKeypadRef {
    sendDigitSequence: (digits: string) => void
}

export const DTMFKeypad = forwardRef<DTMFKeypadRef, DTMFKeypadProps>(({ callStatus, sendDTMF }, ref) => {
    // Just track the pressed digits for display purposes
    const [dtmfHistory, setDtmfHistory] = useState<string>('')
    const [pasteInput, setPasteInput] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)

    const handleDtmfButtonClick = async (digit: string) => {
        setDtmfHistory((prevHistory) => prevHistory + digit)

        // Only process if call is in answered state
        if (callStatus !== CallStatus.CONNECTED) {
            return
        }

        // Update display history

        // Send DTMF tone
        try {
            await sendDTMF(digit)
        } catch (error) {
            console.error('Error::handleDtmfButtonClick::', error)
        }
    }

    // Expose methods via ref for external components to use
    useImperativeHandle(ref, () => ({
        sendDigitSequence: (digits: string) => {
            if (!digits || callStatus !== CallStatus.CONNECTED) return

            // Process each digit with a slight delay to simulate sequential pressing
            const sendDigitsSequentially = async () => {
                for (let i = 0; i < digits.length; i++) {
                    const digit = digits[i]
                    // Only process valid DTMF characters
                    if (/[0-9*#]/.test(digit)) {
                        // Wait a short time between digits to ensure they're processed correctly
                        await new Promise((resolve) => setTimeout(resolve, 300))
                        handleDtmfButtonClick(digit)
                    }
                }
            }

            sendDigitsSequentially()
        },
    }))

    // Handle pasting of DTMF digits
    const handlePasteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Only allow digits, *, and # in the input
        const sanitizedValue = value.replace(/[^0-9*#]/g, '')
        setPasteInput(sanitizedValue)
    }

    const handlePasteSubmit = () => {
        if (!pasteInput || callStatus !== CallStatus.CONNECTED) return

        // Process each digit with a slight delay to simulate sequential pressing
        const sendDigitsSequentially = async () => {
            for (let i = 0; i < pasteInput.length; i++) {
                const digit = pasteInput[i]
                // Wait a short time between digits to ensure they're processed correctly
                await new Promise((resolve) => setTimeout(resolve, 300))
                handleDtmfButtonClick(digit)
            }
            // Clear the input after processing
            setPasteInput('')
        }

        sendDigitsSequentially()
    }

    // Define the keypad layout
    const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

    return (
        <Box sx={{ position: 'relative' }}>
            {!sendDTMF && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        borderRadius: 'sm',
                        zIndex: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'gray',
                    }}
                >
                    Keyboard not available for this call
                </Box>
            )}

            {/* Paste Input Field */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Input
                    ref={inputRef}
                    placeholder="Paste digits here..."
                    value={pasteInput}
                    onChange={handlePasteInputChange}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handlePasteSubmit()
                        }
                    }}
                />
                <Button onClick={handlePasteSubmit} disabled={!pasteInput} variant="solid">
                    Send
                </Button>
            </Box>

            {/* DTMF History Display */}
            <Box
                sx={{
                    p: 1,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    height: '40px',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        overflowX: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        '&::-webkit-scrollbar': {
                            height: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'neutral.outlinedHoverBg',
                            borderRadius: '4px',
                        },
                    }}
                >
                    <Typography
                        level="body-lg"
                        sx={{
                            fontFamily: 'monospace',
                            minHeight: '24px',
                            whiteSpace: 'nowrap',
                            px: 1,
                        }}
                    >
                        {dtmfHistory || 'No digits pressed'}
                    </Typography>
                </Box>
            </Box>

            {/* DTMF Keypad Grid */}
            <Grid container spacing={1}>
                {keypadDigits.map((digit) => (
                    <Grid key={digit} xs={4}>
                        <Button
                            fullWidth
                            variant="soft"
                            onClick={() => handleDtmfButtonClick(digit)}
                            sx={{
                                height: '50px',
                                fontSize: '1.5rem',
                                opacity: callStatus !== CallStatus.CONNECTED && callStatus !== CallStatus.INITIATING ? 0.6 : 1,
                                '&:active': {
                                    transform: callStatus === CallStatus.CONNECTED || callStatus === CallStatus.INITIATING ? 'scale(0.98)' : 'none',
                                    backgroundColor:
                                        callStatus === CallStatus.CONNECTED || callStatus === CallStatus.INITIATING ? 'primary.softHoverBg' : undefined,
                                },
                            }}
                        >
                            {digit}
                        </Button>
                    </Grid>
                ))}
            </Grid>
        </Box>
    )
})
